import {
  PHOTO_BELOW_MINIMUM_REASON,
  photoMeetsMinimumDimensions,
  readImageDimensions,
} from "./photoMinimumDimensions";

export const MAX_PROPERTY_PHOTO_BYTES = 4_000_000;
export const MAX_PROPERTY_PHOTOS = 28;

export const PHOTO_DROPZONE_OPTIONS = {
  multiple: true,
  useFsAccessApi: false,
} as const;

export const PHOTO_INVALID_TYPE_REASON = "o formato precisa ser PNG ou JPG";
export const PHOTO_UPLOAD_FAILED_REASON =
  "houve um erro no servidor. Tente novamente";

export type PropertyPhotoUploadError = {
  name: string;
  reason: string;
};

export function formatPropertyPhotoError(error: PropertyPhotoUploadError): string {
  const reason = error.reason.trim();
  if (!error.name.trim()) {
    return reason;
  }

  const suffix = /[.!?]$/.test(reason) ? "" : ".";
  return `Não foi possível enviar ${error.name}: ${reason}${suffix}`;
}

function readErrorMessage(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const message = (value as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
}

export function getApiPhotoUploadReason(error: unknown): string {
  const axiosData = (error as { response?: { data?: unknown } })?.response?.data;
  return (
    readErrorMessage(axiosData) ||
    readErrorMessage(error) ||
    PHOTO_UPLOAD_FAILED_REASON
  );
}

export function shouldIgnorePhotoDrop(files: File[] | undefined | null): boolean {
  return !Array.isArray(files) || files.length === 0;
}

export function tooManyPropertyPhotosError(
  currentCount: number,
  incomingCount: number
): PropertyPhotoUploadError | null {
  if (
    incomingCount > MAX_PROPERTY_PHOTOS ||
    currentCount === MAX_PROPERTY_PHOTOS ||
    currentCount + incomingCount > MAX_PROPERTY_PHOTOS
  ) {
    return { name: "", reason: "O número máximo de fotos é 28" };
  }

  return null;
}

export async function inspectPropertyPhotoFile(
  file: File
): Promise<PropertyPhotoUploadError | null> {
  if (file.size > MAX_PROPERTY_PHOTO_BYTES) {
    return { name: file.name, reason: "é maior que 4mb" };
  }

  if (file.type !== "image/png" && file.type !== "image/jpeg") {
    return { name: file.name, reason: PHOTO_INVALID_TYPE_REASON };
  }

  try {
    const { width, height } = await readImageDimensions(file);
    if (!photoMeetsMinimumDimensions(width, height)) {
      return { name: file.name, reason: PHOTO_BELOW_MINIMUM_REASON };
    }
  } catch {
    return { name: file.name, reason: "não foi possível ler as dimensões" };
  }

  return null;
}

export function extractPropertyPhotoBase64(dataUrl: string): string | null {
  if (dataUrl.includes("data:image/jpeg;base64,")) {
    return dataUrl.replaceAll("data:image/jpeg;base64,", "");
  }

  if (dataUrl.includes("data:image/png;base64,")) {
    return dataUrl.replaceAll("data:image/png;base64,", "");
  }

  return null;
}

export function mapUploadedPropertyPhoto(raw: {
  id?: number;
  order?: number;
  photo?: string;
  url_l?: string;
  url_m?: string;
  url_s?: string;
}) {
  const url = raw.photo ?? raw.url_m ?? raw.url_l ?? raw.url_s ?? "";
  return {
    id: Number(raw.id),
    order: raw.order != null ? Number(raw.order) : undefined,
    url_l: url,
    url_m: url,
    url_s: url,
  };
}
